import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "latField", "lngField"]
  
  connect() {
    this.selectedIndex = -1
    document.addEventListener("click", this.onClickOutside)
    this.sessionToken = this.generateSessionToken()
    
    this.inputTarget.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  disconnect() {
    document.removeEventListener("click", this.onClickOutside)
    this.inputTarget.removeEventListener("keydown", this.handleKeydown.bind(this))
  }

  onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 2) {
      this.fetchSuggestions(query)
    }
  }

  onInput() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.hideSuggestions()
      return
    }
    
    if (this.hasLatFieldTarget && this.hasLngFieldTarget) {
      this.latFieldTarget.value = ""
      this.lngFieldTarget.value = ""
    }
    
    this.fetchSuggestions(query)
  }

  async fetchSuggestions(query) {
    try {
      const url = this.buildGooglePlacesUrl(query)
      const response = await fetch(url)
      const data = await response.json()
      this.processGoogleResults(data.predictions)
    } catch (error) {
      this.hideSuggestions()
    }
  }

  buildGooglePlacesUrl(query) {
    const baseUrl = "/api/places"
    const params = new URLSearchParams({
      input: query,
      language: "de",
      components: "country:de",
      types: "geocode",
      sessiontoken: this.sessionToken
    })
    return `${baseUrl}?${params.toString()}`
  }

  processGoogleResults(predictions) {
    if (!predictions?.length) {
      this.hideSuggestions()
      return
    }
    this.showGoogleSuggestions(predictions)
  }

  showGoogleSuggestions(predictions) {
    this.selectedIndex = -1
    this.resultsTarget.classList.remove("hidden")
    const listItems = predictions
      .map((p, index) => this.googleSuggestionItem(p, index))
      .join("")
    this.resultsTarget.innerHTML = `<ul class="border rounded-md shadow-lg bg-white z-50">${listItems}</ul>`
  }

  googleSuggestionItem(prediction, index) {
    return `
      <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 suggestion-item" 
          data-index="${index}"
          data-action="click->places-autocomplete#selectSuggestion"
          data-prediction='${JSON.stringify(prediction)}'>
        <div class="font-medium">${prediction.structured_formatting.main_text}</div>
        <div class="text-sm text-gray-500">${prediction.structured_formatting.secondary_text}</div>
      </li>
    `
  }

  async selectSuggestion(event) {
    let prediction
    if (event && event.currentTarget) {
      prediction = JSON.parse(event.currentTarget.dataset.prediction)
    } else if (this.selectedIndex > -1) {
      const suggestionItem = this.resultsTarget.querySelector(
        `li[data-index="${this.selectedIndex}"]`
      )
      if (suggestionItem) {
        prediction = JSON.parse(suggestionItem.dataset.prediction)
      }
    }
    if (!prediction) return

    const details = await this.fetchPlaceDetails(prediction.place_id)
    if (details && details.geometry && details.geometry.location) {
      if (this.hasLatFieldTarget && this.hasLngFieldTarget) {
        this.latFieldTarget.value = details.geometry.location.lat
        this.lngFieldTarget.value = details.geometry.location.lng
        
        this.latFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
        this.lngFieldTarget.dispatchEvent(new Event("change", { bubbles: true }))
      }
      
      this.inputTarget.value = prediction.description
      
      const form = this.element.querySelector('form')
      if (form) {
        setTimeout(() => {
          form.submit()
        }, 50)
      }
    } else {
      this.inputTarget.value = prediction.description
    }
    
    this.sessionToken = this.generateSessionToken()
    this.hideSuggestions()
  }

  async fetchPlaceDetails(placeId) {
    try {
      const params = new URLSearchParams({
        place_id: placeId,
        fields: "geometry,formatted_address",
        sessiontoken: this.sessionToken
      })
      const url = `/api/place_details?${params.toString()}`
      const response = await fetch(url)
      const data = await response.json()
      return data.result || null
    } catch (error) {
      return null
    }
  }

  handleKeydown(event) {
    if (this.resultsTarget.classList.contains("hidden")) return

    const items = Array.from(this.resultsTarget.querySelectorAll("li.suggestion-item"))
    if (!items.length) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (this.selectedIndex < items.length - 1) {
          this.selectedIndex++
        } else {
          this.selectedIndex = 0
        }
        this.updateHighlight(items)
        break
      case "ArrowUp":
        event.preventDefault()
        if (this.selectedIndex > 0) {
          this.selectedIndex--
        } else {
          this.selectedIndex = items.length - 1
        }
        this.updateHighlight(items)
        break
      case "Tab":
        if (items.length > 0) {
          event.preventDefault()
          if (this.selectedIndex < items.length - 1) {
            this.selectedIndex++
          } else {
            this.hideSuggestions()
            return
          }
          this.updateHighlight(items)
        }
        break
      case "Enter":
        if (this.selectedIndex > -1) {
          event.preventDefault()
          this.selectSuggestion()
        }
        break
      case "Escape":
        event.preventDefault()
        this.hideSuggestions()
        break
    }
  }

  updateHighlight(items) {
    items.forEach(item => {
      item.classList.remove("bg-ph24_blue/10")
    })
    
    if (this.selectedIndex >= 0 && this.selectedIndex < items.length) {
      items[this.selectedIndex].classList.add("bg-ph24_blue/10")
    }
  }

  generateSessionToken() {
    return Math.random().toString(36).substring(2, 15)
  }

  hideSuggestions() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = ""
      this.resultsTarget.classList.add("hidden")
    }
    this.selectedIndex = -1
    this.allowNextTab = false
  }

  onClickOutside = (event) => {
    if (this.element && !this.element.contains(event.target)) {
      this.hideSuggestions()
    }
  }
}
